import { useEffect } from 'react';
import { BUSINESS } from '../data/pricing';
import { galleryFor } from '../lib/images';

// Product structured data. This is what makes Google show the price and
// availability directly in the search result — the thing that separates a
// listing people click from one they scroll past. Reliance and Croma both do
// it; it is a large part of why they outrank independent dealers.
//
// Injected as JSON-LD and cleaned up on unmount so it never leaks between
// routes.

export default function ProductSchema({ model, variant, price, inStock }) {
  useEffect(() => {
    if (!model || price == null) return;

    const data = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: model.title,
      // Google accepts an array and prefers several angles. Only the front
      // shot is guaranteed present, so it leads.
      image: galleryFor(model).map((g) => `https://www.citicool.in${g.src}`),
      description: `${model.title}. ${[
        model.variants.withKit && `With kit: ${model.variants.withKit.idu} + ${model.variants.withKit.odu}`,
        model.variants.withoutKit && `Without kit: ${model.variants.withoutKit.idu} + ${model.variants.withoutKit.odu}`,
      ].filter(Boolean).join(' \u00B7 ')}. Rated ${model.kw} kW, ISEER ${model.iseer}, ${model.stars} star BEE rating.`,
      sku: variant.idu,
      mpn: variant.idu,
      brand: { '@type': 'Brand', name: 'Daikin' },
      offers: {
        '@type': 'Offer',
        url: `https://www.citicool.in/${model.slug}`,
        priceCurrency: 'INR',
        price: String(price),
        availability: inStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@type': 'Organization', name: BUSINESS.name },
      },
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Rated cooling capacity', value: `${model.kw} kW` },
        { '@type': 'PropertyValue', name: 'BEE star rating', value: `${model.stars} star` },
        { '@type': 'PropertyValue', name: 'ISEER', value: String(model.iseer) },
        { '@type': 'PropertyValue', name: 'Compressor', value: model.inverter ? 'Inverter' : 'Fixed speed' },
        { '@type': 'PropertyValue', name: 'Refrigerant', value: 'R32' },
      ],
    };

    // BreadcrumbList as well. Google renders the trail in place of a raw URL,
    // which reads better in results and lifts click-through.
    const crumbs = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Daikin AC prices', item: 'https://www.citicool.in/daikin-ac-price-list-delhi' },
        { '@type': 'ListItem', position: 2, name: `${model.soldAs} Ton`, item: `https://www.citicool.in/daikin-${model.soldAs.replace('.', '-')}-ton-ac-price` },
        { '@type': 'ListItem', position: 3, name: model.title, item: `https://www.citicool.in/${model.slug}` },
      ],
    };

    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify(data);
    el.setAttribute('data-product-schema', model.id);
    document.head.appendChild(el);

    const bc = document.createElement('script');
    bc.type = 'application/ld+json';
    bc.textContent = JSON.stringify(crumbs);
    bc.setAttribute('data-breadcrumb-schema', model.id);
    document.head.appendChild(bc);

    return () => { el.remove(); bc.remove(); };
  }, [model, variant, price, inStock]);

  return null;
}